import {DeleteSiteModal} from '@/components/sites/DeleteSiteModal';
import {ProvisioningAnimation} from '@/components/sites/ProvisioningAnimation';
import {ProvisioningSteps} from '@/components/sites/ProvisioningSteps';
import {SITE_DELETION_PENDING_MESSAGE, getSSOLink} from '@/api/sites';
import {Spinner} from '@/components/ui/Spinner';
import {getSitePublicUrl} from '@/lib/site-url';
import {useDeleteSite} from '@/hooks/useDeleteSite';
import {useMediaQuery} from '@/hooks/useMediaQuery';
import {useNavigate, useParams} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {useRetrySite} from '@/hooks/useRetrySite';
import {useSite} from '@/hooks/useSite';
import {useSiteStatus} from '@/hooks/useSiteStatus';
import {useState} from 'react';

export function ProvisioningPage() {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {isMobile} = useMediaQuery();
    const {data: siteStatus, isLoading, error} = useSiteStatus(id || '');
    const {data: site} = useSite(id || '');
    const retrySite = useRetrySite();
    const deleteSite = useDeleteSite();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [ssoLoading, setSsoLoading] = useState(false);
    const [deletionStarted, setDeletionStarted] = useState(false);
    const deletionPending = deletionStarted || site?.deletion_pending;

    const isComplete = siteStatus?.status === 'active';
    const isFailed = siteStatus?.status === 'failed' || siteStatus?.provisioning_status === 'failed';
    const publicUrl = site ? getSitePublicUrl(site) : undefined;

    const openNewsletterAdmin = async () => {
        if (!id || ssoLoading) {
            return;
        }

        queryClient.cancelQueries(['site', id]);
        queryClient.removeQueries(['site', id]);
        queryClient.invalidateQueries(['sites']);
        setSsoLoading(true);

        // Open the tab synchronously so popup blockers don't kill it during the async SSO fetch.
        const adminTab = window.open('', '_blank');
        if (adminTab) {
            adminTab.opener = null;
        }

        try {
            const url = await getSSOLink(id);
            if (adminTab) {
                adminTab.location.href = url;
            } else {
                window.open(url, '_blank');
            }
        } catch {
            const retryUrl = new URL(`/sites/${id}/open?error=sso`, window.location.origin).href;
            if (adminTab) {
                adminTab.location.href = retryUrl;
            } else {
                navigate(`/sites/${id}/open?error=sso`);
            }
        } finally {
            setSsoLoading(false);
        }
    };

    return (
        <div
            className="shade-control-panel studio-content-page studio-provisioning-page"
            style={{flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--tt-main-bg)', fontFamily: 'inherit', maxWidth: 'none', margin: 0}}
        >
            <style>{`
                @keyframes prov-fade-in {
                    from { opacity: 0; transform: translateY(16px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes prov-scale-in {
                    from { opacity: 0; transform: scale(0.7); }
                    to { opacity: 1; transform: scale(1); }
                }
            `}</style>

            {(isLoading || !siteStatus) && (
                <Spinner size={64} />
            )}

            {!!error && (
                <div style={{textAlign: 'center', maxWidth: 400}}>
                    <p style={{fontSize: isMobile ? '1.6rem' : '1.8rem', fontWeight: 600, color: 'var(--tt-text-primary)', marginBottom: 8}}>
                        Something went wrong
                    </p>
                    <p style={{fontSize: isMobile ? '1.3rem' : '1.4rem', color: 'var(--tt-text-secondary)', marginBottom: isMobile ? 20 : 24}}>
                        We couldn&apos;t load the setup progress. Please try again.
                    </p>
                    <button className="studio-control studio-control-primary" style={{cursor: 'pointer', fontFamily: 'inherit'}}
                        type="button"
                        onClick={() => navigate('/dashboard')}
                    >
                        Back to dashboard
                    </button>
                </div>
            )}

            {!deletionPending && siteStatus && !isComplete && !isFailed && (
                <div style={{textAlign: 'center', maxWidth: 520, width: '100%'}}>
                    <div style={{marginBottom: isMobile ? 20 : 28}}>
                        <ProvisioningAnimation complete={false} step={siteStatus.provisioning_step}/>
                    </div>
                    <h1 className="studio-page-title">
                        Getting your newsletter ready&hellip;
                    </h1>
                    <p
                        style={{
                            fontSize: isMobile ? '1.4rem' : '1.5rem',
                            color: 'var(--tt-text-secondary)',
                            margin: isMobile ? '12px 0 0' : '16px 0 0',
                            fontWeight: 400,
                            lineHeight: 1.5
                        }}
                    >
                        You can leave this page. We&apos;ll keep working in the background.
                    </p>
                    <div style={{marginTop: isMobile ? 28 : 36, textAlign: 'left'}}>
                        <ProvisioningSteps siteStatus={siteStatus}/>
                    </div>
                </div>
            )}

            {!deletionPending && siteStatus && isComplete && (
                <div
                    style={{
                        textAlign: 'center',
                        maxWidth: 480,
                        width: '100%'
                    }}
                >
                    <div style={{marginBottom: isMobile ? 20 : 24, animation: 'prov-scale-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both'}}>
                        <ProvisioningAnimation complete={true}/>
                    </div>

                    <h1 className="studio-page-title">
                        Your newsletter is ready!
                    </h1>

                    <p
                        style={{
                            fontSize: isMobile ? '1.4rem' : '1.5rem',
                            color: 'var(--tt-text-secondary)',
                            margin: isMobile ? '12px 0 28px' : '16px 0 36px',
                            fontWeight: 400,
                            lineHeight: 1.5,
                            animation: 'prov-fade-in 0.5s ease 0.25s both'
                        }}
                    >
                        Everything is set up and your newsletter is now live. You can start publishing right away.
                    </p>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: 12,
                            justifyContent: 'center',
                            animation: 'prov-fade-in 0.5s ease 0.35s both'
                        }}
                    >
                        <a className="studio-control studio-control-primary" href={publicUrl} rel="noopener noreferrer" style={{fontFamily: 'inherit', minWidth: isMobile ? 'auto' : 180, textDecoration: 'none'}} target="_blank"
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--tt-accent-hover)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--tt-accent)';
                            }}
                        >
                            View your newsletter
                        </a>
                        <button className="studio-control" disabled={ssoLoading}
                            style={{height: isMobile ? 44 : 48, padding: isMobile ? '0 24px' : '0 32px', borderRadius: 8, background: 'var(--tt-card-bg)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, border: '1px solid var(--tt-card-border)', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.15s ease', boxSizing: 'border-box', minWidth: isMobile ? 'auto' : 180}}
                            type="button"
                            onClick={openNewsletterAdmin}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--tt-nav-hover-bg)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--tt-card-bg)';
                            }}
                        >
                            {ssoLoading ? 'Opening...' : 'Start writing'}
                        </button>
                    </div>
                </div>
            )}

            {!deletionPending && siteStatus && isFailed && (
                <div style={{textAlign: 'center', maxWidth: 420}}>
                    <div
                        style={{
                            width: isMobile ? 56 : 72,
                            height: isMobile ? 56 : 72,
                            borderRadius: '50%',
                            background: 'rgba(245, 11, 35, 0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: isMobile ? '0 auto 20px' : '0 auto 24px'
                        }}
                    >
                        <svg fill="none" height={isMobile ? 24 : 32} viewBox="0 0 24 24" width={isMobile ? 24 : 32}>
                            <path d="M18 6L6 18M6 6L18 18" stroke="var(--tt-red)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"/>
                        </svg>
                    </div>
                    <h1 className="studio-page-title">
                        Setup couldn&rsquo;t be completed
                    </h1>
                    <p
                        style={{
                            fontSize: isMobile ? '1.4rem' : '1.5rem',
                            color: 'var(--tt-text-secondary)',
                            margin: '0 0 16px',
                            fontWeight: 400,
                            lineHeight: 1.5
                        }}
                    >
                        We ran into an issue while setting up your newsletter. You can retry the setup or delete this newsletter and start fresh.
                    </p>
                    {siteStatus.provisioning_error && (
                        <div
                            style={{
                                textAlign: 'left',
                                background: 'rgba(245, 11, 35, 0.04)',
                                border: '1px solid rgba(245, 11, 35, 0.15)',
                                borderRadius: 8,
                                padding: isMobile ? '12px 14px' : '14px 16px',
                                margin: isMobile ? '0 0 24px' : '0 0 32px'
                            }}
                        >
                            {siteStatus.provisioning_step && (
                                <p style={{
                                    fontSize: '1.2rem',
                                    fontWeight: 600,
                                    color: 'var(--tt-red)',
                                    margin: '0 0 6px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em'
                                }}>
                                    Failed at: {siteStatus.provisioning_step.replace(/_/g, ' ')}
                                </p>
                            )}
                            <p style={{
                                fontSize: '1.3rem',
                                color: 'var(--tt-text-primary)',
                                margin: 0,
                                lineHeight: 1.5,
                                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                                wordBreak: 'break-word'
                            }}>
                                {siteStatus.provisioning_error}
                            </p>
                        </div>
                    )}
                    {!siteStatus.provisioning_error && (
                        <div style={{marginBottom: isMobile ? 24 : 32}} />
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: 12,
                            justifyContent: 'center'
                        }}
                    >
                        <button className="studio-control studio-control-primary" disabled={retrySite.isLoading}
                            style={{cursor: retrySite.isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', minWidth: isMobile ? 'auto' : 150, opacity: retrySite.isLoading ? 0.6 : 1}}
                            type="button"
                            onClick={() => {
                                retrySite.mutate(id!);
                            }}
                            onMouseEnter={(e) => {
                                if (!retrySite.isLoading) {
                                    e.currentTarget.style.backgroundColor = 'var(--tt-accent-hover)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--tt-accent)';
                            }}
                        >
                            {retrySite.isLoading ? 'Retrying...' : 'Retry setup'}
                        </button>

                        <button className="studio-control" style={{height: isMobile ? 44 : 48, padding: isMobile ? '0 24px' : '0 32px', borderRadius: 8, background: 'var(--tt-card-bg)', color: 'var(--tt-red)', fontSize: '13px', fontWeight: 500, border: '1px solid rgba(245, 11, 35, 0.2)', cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color 0.15s ease', minWidth: isMobile ? 'auto' : 150}}
                            type="button"
                            onClick={() => setShowDeleteModal(true)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(245, 11, 35, 0.04)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--tt-card-bg)';
                            }}
                        >
                            Delete newsletter
                        </button>
                    </div>

                    <button style={{
                        marginTop: isMobile ? 16 : 20,
                        background: 'none',
                        border: 'none',
                        color: 'var(--tt-text-secondary)',
                        fontSize: isMobile ? '1.3rem' : '1.4rem',
                        fontWeight: 400,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textDecoration: 'underline',
                        textUnderlineOffset: 3
                    }}
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    >
                        Back to dashboard
                    </button>
                </div>
            )}

            {deletionPending && (
                <div className="studio-panel">
                    <span className="studio-access-label">Deletion pending</span>
                    <p className="studio-page-description" role="status">{SITE_DELETION_PENDING_MESSAGE}</p>
                    <button className="studio-control" type="button" onClick={() => navigate('/dashboard')}>Back to dashboard</button>
                </div>
            )}

            {showDeleteModal && !deletionPending && (
                <DeleteSiteModal
                    isLoading={deleteSite.isLoading}
                    siteName={site?.name || 'this newsletter'}
                    onCancel={() => setShowDeleteModal(false)}
                    onConfirm={() => {
                        deleteSite.mutate(id!, {
                            onSuccess: (result) => {
                                if (result?.status === 'deletion_pending') {
                                    setDeletionStarted(true);
                                    setShowDeleteModal(false);
                                } else {
                                    navigate('/dashboard');
                                }
                            }
                        });
                    }}
                />
            )}
        </div>
    );
}
